import { createContext } from "react";
import { type Store } from "./common";
import { createConsumers } from "./consumers";
import { makeStoreProvider } from "./StoreProvider";
import {
  type ConsumerGetterConfig,
  type ConsumerMutationConfig,
  type ConsumerQueryConfig,
  type ConsumerSetterConfig,
  type StoreValue,
} from "./types";

export function createStore<TStoreBaseValue>() {
  function createStoreBuilder<
    TConsumerGetterConfig extends ConsumerGetterConfig<TStoreBaseValue>,
    TConsumerSetterConfig extends ConsumerSetterConfig<TStoreBaseValue>,
    TConsumerQueryConfig extends ConsumerQueryConfig<TStoreBaseValue, TConsumerSetterConfig>,
    TConsumerMutationConfig extends ConsumerMutationConfig<TStoreBaseValue, TConsumerSetterConfig>,
  >(actionsConfig: {
    getters: TConsumerGetterConfig;
    setters: TConsumerSetterConfig;
    queries: TConsumerQueryConfig;
    mutations: TConsumerMutationConfig;
  }) {
    const context = createContext<Store<StoreValue<TStoreBaseValue>>>(
      undefined as unknown as Store<StoreValue<TStoreBaseValue>>,
    );

    return {
      StoreProvider: makeStoreProvider(context),
      ...createConsumers(context, actionsConfig),
      addGetters<TNextGetterConfig extends ConsumerGetterConfig<TStoreBaseValue>>(
        nextGetters: TNextGetterConfig,
      ) {
        return createStoreBuilder<TNextGetterConfig, TConsumerSetterConfig, any, any>({
          ...actionsConfig,
          getters: nextGetters,
        });
      },
      addSetters<TNextSetterConfig extends ConsumerSetterConfig<TStoreBaseValue>>(
        nextSetters: TNextSetterConfig,
      ) {
        return createStoreBuilder<TConsumerGetterConfig, TNextSetterConfig, any, any>({
          ...actionsConfig,
          setters: nextSetters,
        });
      },
      addQueries<
        TNextQueryConfig extends ConsumerQueryConfig<TStoreBaseValue, TConsumerSetterConfig>,
      >(nextQueries: TNextQueryConfig) {
        return createStoreBuilder<
          TConsumerGetterConfig,
          TConsumerSetterConfig,
          TNextQueryConfig,
          TConsumerMutationConfig
        >({
          ...actionsConfig,
          queries: nextQueries,
        });
      },
      addMutations<
        TNextMutationConfig extends ConsumerMutationConfig<TStoreBaseValue, TConsumerSetterConfig>,
      >(nextMutations: TNextMutationConfig) {
        return createStoreBuilder<
          TConsumerGetterConfig,
          TConsumerSetterConfig,
          TConsumerQueryConfig,
          TNextMutationConfig
        >({
          ...actionsConfig,
          mutations: nextMutations,
        });
      },
    };
  }
  return createStoreBuilder({
    getters: {},
    setters: {},
    queries: {},
    mutations: {},
  });
}
